import { memo, useEffect, useRef } from 'react';
import { Animated, View, type ViewStyle } from 'react-native';
import Svg, {
  Defs,
  G,
  Line,
  LinearGradient,
  Path,
  Rect,
  Stop,
  Text as SvgText,
} from 'react-native-svg';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import {
  type DataPoint,
  formatTickLabel,
  generateTicks,
  generateUniqueId,
  getDataRange,
  getPlotArea,
  linearScale,
  niceMax,
} from './chart-utils';

interface BarChartProps {
  data: DataPoint[];
  width?: number;
  height?: number;
  color?: string;
  barWidth?: number;
  showLabels?: boolean;
  showValues?: boolean;
  showGrid?: boolean;
  // Grid LINES and the axis SCALE are separate concerns. They used to share
  // `showGrid`, so a caller that wanted an uncluttered panel lost the numbers
  // too and drew bars a reader could not put a value to. `LineChartProps` has
  // kept the two apart all along; this matches it.
  showYAxisLabels?: boolean;
  gradientColors?: readonly [string, string];
  animate?: boolean;
  animationDuration?: number;
  horizontal?: boolean;
  barRadius?: number;
  gridLineCount?: number;
  style?: ViewStyle;
}

const VERTICAL_PADDING = { top: 20, right: 12, bottom: 28, left: 44 };
const HORIZONTAL_PADDING = { top: 12, right: 40, bottom: 12, left: 80 };

const AXIS_LABEL_FONT_SIZE = 10;
const BAR_FADE_OPACITY = 0.13;

function topRoundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.min(r, w / 2, h);
  return [
    `M${x},${y + h}`,
    `L${x},${y + radius}`,
    `Q${x},${y} ${x + radius},${y}`,
    `L${x + w - radius},${y}`,
    `Q${x + w},${y} ${x + w},${y + radius}`,
    `L${x + w},${y + h}`,
    'Z',
  ].join(' ');
}

function BarChartInner({
  data,
  width = 300,
  height = 200,
  color = colors.primary.neon,
  barWidth,
  showLabels = true,
  showValues = true,
  showGrid = true,
  showYAxisLabels = true,
  gradientColors,
  animate = true,
  animationDuration = 600,
  horizontal = false,
  barRadius = 6,
  gridLineCount = 5,
  style,
}: Readonly<BarChartProps>) {
  const reducedMotion = useReducedMotion();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const gradientId = useRef(generateUniqueId('bar-grad')).current;
  const shouldAnimate = animate && !reducedMotion;

  useEffect(() => {
    if (shouldAnimate) {
      fadeAnim.setValue(0);
      scaleAnim.setValue(0);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: animationDuration * 0.4,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: animationDuration,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      fadeAnim.setValue(1);
      scaleAnim.setValue(1);
    }
  }, [shouldAnimate, animationDuration, fadeAnim, scaleAnim]);

  if (!data || data.length === 0) {
    return <View style={[{ width, height }, style]} />;
  }

  const padding = horizontal ? HORIZONTAL_PADDING : VERTICAL_PADDING;
  const plotArea = getPlotArea({ width, height, padding });
  const { max: dataMax } = getDataRange(data);
  const maxValue = niceMax(dataMax);
  const explicitGradient = !!gradientColors;
  const verticalFadeGradient = !horizontal && !explicitGradient;
  const hasGradient = explicitGradient || verticalFadeGradient;
  const barFill = hasGradient ? `url(#${gradientId})` : color;

  const ticks = generateTicks(0, maxValue, gridLineCount);
  const chartLabel = `Bar chart: ${data.length} items, max value ${formatTickLabel(dataMax)}`;

  return (
    <Animated.View
      style={[{ width, height, opacity: fadeAnim }, style]}
      accessibilityRole="image"
      accessibilityLabel={chartLabel}
    >
      <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {hasGradient && (
          <Defs>
            <LinearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2={horizontal ? '100%' : '0%'}
              y2={horizontal ? '0%' : '100%'}
            >
              {explicitGradient && gradientColors
                ? [
                    <Stop key="start" offset="0%" stopColor={gradientColors[0]} stopOpacity={1} />,
                    <Stop key="end" offset="100%" stopColor={gradientColors[1]} stopOpacity={1} />,
                  ]
                : [
                    <Stop key="start" offset="0%" stopColor={color} stopOpacity={1} />,
                    <Stop
                      key="end"
                      offset="100%"
                      stopColor={color}
                      stopOpacity={BAR_FADE_OPACITY}
                    />,
                  ]}
            </LinearGradient>
          </Defs>
        )}

        {horizontal ? (
          <HorizontalBars
            data={data}
            plotArea={plotArea}
            maxValue={maxValue}
            barWidth={barWidth}
            barRadius={barRadius}
            color={color}
            fillColor={barFill}
            showLabels={showLabels}
            showValues={showValues}
            showGrid={showGrid}
            showYAxisLabels={showYAxisLabels}
            ticks={ticks}
          />
        ) : (
          <VerticalBars
            data={data}
            plotArea={plotArea}
            maxValue={maxValue}
            barWidth={barWidth}
            barRadius={barRadius}
            color={color}
            fillColor={barFill}
            showLabels={showLabels}
            showValues={showValues}
            showGrid={showGrid}
            showYAxisLabels={showYAxisLabels}
            ticks={ticks}
            height={height}
          />
        )}
      </Svg>
    </Animated.View>
  );
}

function VerticalBars({
  data,
  plotArea,
  maxValue,
  barWidth: customBarWidth,
  barRadius,
  fillColor,
  showLabels,
  showValues,
  showGrid,
  showYAxisLabels,
  ticks,
  height,
}: Readonly<{
  data: DataPoint[];
  plotArea: ReturnType<typeof getPlotArea>;
  maxValue: number;
  barWidth?: number;
  barRadius: number;
  color: string;
  fillColor: string;
  showLabels: boolean;
  showValues: boolean;
  showGrid: boolean;
  showYAxisLabels: boolean;
  ticks: number[];
  height: number;
}>) {
  const gap = Math.max(4, (plotArea.width * 0.1) / data.length);
  const computedBarWidth =
    customBarWidth ?? Math.max(8, (plotArea.width - gap * (data.length + 1)) / data.length);

  return (
    <G>
      {(showGrid || showYAxisLabels) &&
        ticks.map((tick) => {
          const y =
            plotArea.y + plotArea.height - linearScale(tick, 0, maxValue, 0, plotArea.height);
          return (
            <G key={`vgrid-${tick}`}>
              {showGrid && (
                <Line
                  x1={plotArea.x}
                  y1={y}
                  x2={plotArea.x + plotArea.width}
                  y2={y}
                  stroke={colors.border.subtle}
                  strokeWidth={1}
                />
              )}
              {showYAxisLabels && (
                <SvgText
                  x={plotArea.x - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontFamily={darkPrecisionFontFamilies.metric}
                  fontSize={AXIS_LABEL_FONT_SIZE}
                  fill={colors.text.tertiary}
                >
                  {formatTickLabel(tick)}
                </SvgText>
              )}
            </G>
          );
        })}

      {data.map((point, index) => {
        const barH = linearScale(point.value, 0, maxValue, 0, plotArea.height);
        const totalBarsWidth = computedBarWidth * data.length + gap * (data.length - 1);
        const startX = plotArea.x + (plotArea.width - totalBarsWidth) / 2;
        const x = startX + index * (computedBarWidth + gap);
        const y = plotArea.y + plotArea.height - barH;

        const safeBarH = Math.max(0, barH);

        return (
          <G key={`bar-${point.label}`}>
            <Path
              d={topRoundedRectPath(x, y, computedBarWidth, safeBarH, barRadius)}
              fill={fillColor}
            />
            {showValues && barH > 16 && (
              <SvgText
                x={x + computedBarWidth / 2}
                y={y - 6}
                textAnchor="middle"
                fontFamily={darkPrecisionFontFamilies.metric}
                fontSize={AXIS_LABEL_FONT_SIZE}
                fill={colors.text.secondary}
              >
                {formatTickLabel(point.value)}
              </SvgText>
            )}
            {showLabels && (
              <SvgText
                x={x + computedBarWidth / 2}
                y={height - 4}
                textAnchor="middle"
                fontFamily={darkPrecisionFontFamilies.metric}
                fontSize={AXIS_LABEL_FONT_SIZE}
                fill={colors.text.tertiary}
              >
                {point.label}
              </SvgText>
            )}
          </G>
        );
      })}
    </G>
  );
}

function HorizontalBars({
  data,
  plotArea,
  maxValue,
  barWidth: customBarWidth,
  barRadius,
  fillColor,
  showLabels,
  showValues,
  showGrid,
  showYAxisLabels,
  ticks,
}: Readonly<{
  data: DataPoint[];
  plotArea: ReturnType<typeof getPlotArea>;
  maxValue: number;
  barWidth?: number;
  barRadius: number;
  color: string;
  fillColor: string;
  showLabels: boolean;
  showValues: boolean;
  showGrid: boolean;
  showYAxisLabels: boolean;
  ticks: number[];
}>) {
  const gap = Math.max(4, (plotArea.height * 0.1) / data.length);
  const computedBarHeight =
    customBarWidth ?? Math.max(8, (plotArea.height - gap * (data.length + 1)) / data.length);

  return (
    <G>
      {(showGrid || showYAxisLabels) &&
        ticks.map((tick) => {
          const x = plotArea.x + linearScale(tick, 0, maxValue, 0, plotArea.width);
          return (
            <G key={`hgrid-${tick}`}>
              {showGrid && (
                <Line
                  x1={x}
                  y1={plotArea.y}
                  x2={x}
                  y2={plotArea.y + plotArea.height}
                  stroke={colors.border.subtle}
                  strokeWidth={1}
                />
              )}
              {showYAxisLabels && (
                <SvgText
                  x={x}
                  y={plotArea.y + plotArea.height + 16}
                  textAnchor="middle"
                  fontFamily={darkPrecisionFontFamilies.metric}
                  fontSize={AXIS_LABEL_FONT_SIZE}
                  fill={colors.text.tertiary}
                >
                  {formatTickLabel(tick)}
                </SvgText>
              )}
            </G>
          );
        })}

      {data.map((point, index) => {
        const barW = linearScale(point.value, 0, maxValue, 0, plotArea.width);
        const totalBarsHeight = computedBarHeight * data.length + gap * (data.length - 1);
        const startY = plotArea.y + (plotArea.height - totalBarsHeight) / 2;
        const y = startY + index * (computedBarHeight + gap);

        return (
          <G key={`hbar-${point.label}`}>
            <Rect
              x={plotArea.x}
              y={y}
              width={Math.max(0, barW)}
              height={computedBarHeight}
              fill={fillColor}
              rx={barRadius}
              ry={barRadius}
            />
            {showValues && (
              <SvgText
                x={plotArea.x + barW + 6}
                y={y + computedBarHeight / 2 + 4}
                textAnchor="start"
                fontFamily={darkPrecisionFontFamilies.metric}
                fontSize={AXIS_LABEL_FONT_SIZE}
                fill={colors.text.secondary}
              >
                {formatTickLabel(point.value)}
              </SvgText>
            )}
            {showLabels && (
              <SvgText
                x={plotArea.x - 6}
                y={y + computedBarHeight / 2 + 4}
                textAnchor="end"
                fontFamily={darkPrecisionFontFamilies.metric}
                fontSize={AXIS_LABEL_FONT_SIZE}
                fill={colors.text.tertiary}
              >
                {point.label}
              </SvgText>
            )}
          </G>
        );
      })}
    </G>
  );
}

export const BarChart = memo(BarChartInner);
export type { BarChartProps };
